import React, { useCallback, useMemo, useReducer } from "react";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import { UserDTO } from "@generated/v2";
import { getPeriodFromToday } from "app/helpers/utilHelper";
import { UserInfoFiltersType } from "app/types/filterType";
import { PeriodsEnum } from "app/constants/dateConst";
import {
  DEFAULT_TEST_RESULTS,
  DEFAULT_CONTROL_RESULTS,
} from "app/constants/filterConst";

const USER_KEY = "userView";

type UserContextViewProps = UserDTO & {
  filters: UserInfoFiltersType;
  refetchFilters?: {
    refetchCustomer: () => void;
    refetchSite: () => void;
  };
  authenticated?: boolean;
  authenticationError?: string;
};

type UserContextState = {
  userInfoView: UserContextViewProps;
  setUserInfoView: React.Dispatch<Partial<UserContextViewProps>>;
};

const reducer = (
  userInfoView: UserContextViewProps,
  setUserInfoView: Partial<UserContextViewProps>
) => {
  if (isEmpty(setUserInfoView)) {
    /**
     * TODO: We should clear the whole UserContext. But there seems
     * to be a TypeScript issue, when you try and return and empty
     * when using a reducer.
     */
    return { ...userInfoView, filters: {} };
  }
  return { ...userInfoView, ...setUserInfoView };
};

const initialState = {
  filters: {
    period: PeriodsEnum.MONTHS_6,
    dates: getPeriodFromToday(),
    routes: [] as string[],
    testResult: DEFAULT_TEST_RESULTS,
    controlResult: DEFAULT_CONTROL_RESULTS,
    userIds: [],
  },
};

const UserViewContext = React.createContext({} as UserContextState);

function UserViewProvider({ children }) {
  const [userInfoView, setUserInfoViewFn] = useReducer(
    reducer,
    initialState,
    state => {
      const sessionUser = sessionStorage.getItem(USER_KEY);
      const sessionState = sessionUser
        ? JSON.parse(sessionUser, (name, value) => {
            if (name === "filters") {
              return {
                ...value,
                dates: value.dates?.map(date => moment(date)),
              };
            }
            return value;
          })
        : null;
      return sessionState ?? state;
    }
  );

  const setUserInfoView: React.Dispatch<Partial<UserContextViewProps>> =
    useCallback(newInfo => {
      sessionStorage.setItem(USER_KEY, JSON.stringify(newInfo));
      setUserInfoViewFn(newInfo);
    }, []);

  const value = useMemo(
    () => ({ userInfoView, setUserInfoView }),
    [setUserInfoView, userInfoView]
  );

  return (
    <UserViewContext.Provider value={value}>
      {children}
    </UserViewContext.Provider>
  );
}

export { UserViewContext, UserViewProvider };
